import React, { useState, useContext } from "react"
import i18n from "../../services/i18n"
import {
  Modal,
  Input,
  Button,
  DatePicker,
  List,
  Tag,
  message,
  Typography,
  Select,
  Radio,
  Popconfirm,
  Checkbox,
  Spin,
  Empty,
} from "antd"
import { Clipboard, Plus, Trash2, Check, X, Eye, Search } from "react-feather"
import moment from "moment"
import { useQuery, useMutation } from "@tanstack/react-query"
import { GET_MANAGERS } from "../../pages/managers/queries"
import { AuthContext } from "../../contexts/auth/AuthContext"
import { ThemeContext } from "../../contexts/ThemeContex"
import {
  GET_TASKS,
  CREATE_TASK,
  UPDATE_TASK_STATUS,
  MARK_TASK_READ,
  REVIEW_TASK,
  DELETE_TASK,
} from "../../rest-api/queries/tasks"
import { playNotificationSound } from "../../utils/notificationSounds"
import { useTranslation } from "react-i18next"
import { translateText, currentLang, SupportedLang } from "../../utils/aiTranslate"

export default function DailyPlanModal({
  visible,
  onClose,
}: {
  visible: boolean
  onClose: () => void
}) {
  const { authState } = useContext(AuthContext)
  const { themeState } = useContext(ThemeContext)
  const isDark = themeState.currentTheme === "dark"
  const isSuperAdmin = authState.user?.userName === "Administrator"
  const { t } = useTranslation()

  // --- Filtrlar (talabnoma, 6-bo'lim) ---
  const [filterStatus, setFilterStatus] = useState<string>("all")
  const [filterManagerId, setFilterManagerId] = useState<number | undefined>(undefined)
  const [createdRange, setCreatedRange] = useState<
    [moment.Moment | null, moment.Moment | null] | null
  >(null)
  const [deadlineRange, setDeadlineRange] = useState<
    [moment.Moment | null, moment.Moment | null] | null
  >(null)
  const [search, setSearch] = useState("")
  const [searchTerm, setSearchTerm] = useState("")

  // --- Yangi topshiriq formasi ---
  const [taskTitle, setTaskTitle] = useState("")
  const [taskDesc, setTaskDesc] = useState("")
  const [assignedManagerIds, setAssignedManagerIds] = useState<number[]>([])
  const [dueDate, setDueDate] = useState<moment.Moment | null>(moment().add(1, "day"))
  const [priority, setPriority] = useState<string>("medium")

  // --- Menejer hisoboti (bajarildi / bajarilmadi sababi) ---
  const [commentTaskId, setCommentTaskId] = useState<number | null>(null)
  const [commentStatus, setCommentStatus] = useState<string>("completed")
  const [commentText, setCommentText] = useState("")
  const [failureCategory, setFailureCategory] = useState<string>("other")
  const [isPartial, setIsPartial] = useState(false)

  // --- Admin hisobotni tekshirishi (4-bosqich) ---
  const [reviewTaskId, setReviewTaskId] = useState<number | null>(null)
  const [reviewComment, setReviewComment] = useState("")

  // --- Admin yozgan matnni saqlashdan oldin tarjima qilish ---
  const [composeLang, setComposeLang] = useState<SupportedLang>("ru")
  const [composeTranslating, setComposeTranslating] = useState(false)

  const translateCompose = async () => {
    if (!taskTitle.trim() && !taskDesc.trim()) {
      message.warning(t("enterTextFirst"))
      return
    }
    setComposeTranslating(true)
    try {
      const [title, desc] = await Promise.all([
        taskTitle.trim() ? translateText(taskTitle, composeLang) : Promise.resolve(taskTitle),
        taskDesc.trim() ? translateText(taskDesc, composeLang) : Promise.resolve(taskDesc),
      ])
      setTaskTitle(title)
      setTaskDesc(desc)
    } finally {
      setComposeTranslating(false)
    }
  }

  // --- Topshiriq matnini AI orqali o'z tiliga o'girish ---
  const [taskTranslations, setTaskTranslations] = useState<Record<number, string>>({})
  const [translatingTaskId, setTranslatingTaskId] = useState<number | null>(null)

  const toggleTaskTranslate = async (id: number, text: string) => {
    if (taskTranslations[id]) {
      setTaskTranslations((prev) => {
        const next = { ...prev }
        delete next[id]
        return next
      })
      return
    }
    setTranslatingTaskId(id)
    try {
      const result = await translateText(text || "", currentLang())
      setTaskTranslations((prev) => ({ ...prev, [id]: result }))
    } finally {
      setTranslatingTaskId(null)
    }
  }

  const { data: managersData } = useQuery(
    ["GET_MANAGERS", ""],
    () => GET_MANAGERS({ term: "" }),
    { enabled: visible && isSuperAdmin }
  )
  const managersList = managersData?.data || managersData || []

  const {
    data: tasksData,
    isLoading: loadingTasks,
    refetch: refetchTasks,
  } = useQuery(
    [
      "GET_TASKS",
      filterStatus,
      filterManagerId,
      createdRange?.[0]?.format("YYYY-MM-DD"),
      createdRange?.[1]?.format("YYYY-MM-DD"),
      deadlineRange?.[0]?.format("YYYY-MM-DD"),
      deadlineRange?.[1]?.format("YYYY-MM-DD"),
      searchTerm,
    ],
    () =>
      GET_TASKS({
        status: filterStatus,
        managerId: filterManagerId,
        begin: createdRange?.[0]?.startOf("day").toISOString(),
        end: createdRange?.[1]?.endOf("day").toISOString(),
        deadlineBegin: deadlineRange?.[0]?.startOf("day").toISOString(),
        deadlineEnd: deadlineRange?.[1]?.endOf("day").toISOString(),
        search: searchTerm || undefined,
        pageNumber: 1,
        pageSize: 200,
      }),
    { enabled: visible }
  )
  const tasksList: any[] = tasksData?.data || tasksData?.Data || []

  const { mutate: createTaskMutation, isLoading: creating } = useMutation(CREATE_TASK, {
    onSuccess: () => {
      message.success(t("taskCreated"))
      setTaskTitle("")
      setTaskDesc("")
      setAssignedManagerIds([])
      refetchTasks()
      playNotificationSound()
    },
    onError: (err: any) => {
      message.error(
        t("errCreateTask") + ": " + (err?.response?.data?.Message || err.message)
      )
    },
  })

  const { mutate: updateStatusMutation, isLoading: updatingStatus } = useMutation(
    UPDATE_TASK_STATUS,
    {
      onSuccess: () => {
        message.success(t("statusUpdated"))
        setCommentTaskId(null)
        setCommentText("")
        refetchTasks()
      },
      onError: (err: any) => {
        message.error(
          t("errUpdateStatus") + ": " + (err?.response?.data?.Message || err.message)
        )
      },
    }
  )

  const { mutate: markReadMutation } = useMutation(MARK_TASK_READ, {
    onSuccess: () => {
      message.success(t("markedAsRead"))
      refetchTasks()
    },
    onError: (err: any) => {
      message.error(t("errGeneric") + ": " + (err?.response?.data?.Message || err.message))
    },
  })

  const { mutate: deleteTaskMutation } = useMutation(DELETE_TASK, {
    onSuccess: () => {
      message.info(t("taskDeleted"))
      refetchTasks()
    },
  })

  // Admin qarori: tasdiqlash yoki qayta ishlashga qaytarish
  const { mutate: reviewTaskMutation, isLoading: reviewing } = useMutation(REVIEW_TASK, {
    onSuccess: (_res, vars: any) => {
      message.success(
        vars?.approve
          ? String(t("reportApproved"))
          : String(t("taskReturned"))
      )
      setReviewTaskId(null)
      setReviewComment("")
      refetchTasks()
    },
    onError: (err: any) => {
      message.error(
        t("errReview") + ": " + (err?.response?.data?.Message || err.message)
      )
    },
  })

  const handleCreate = () => {
    if (!taskTitle.trim()) {
      message.warning(t("enterTaskTitle"))
      return
    }
    if (assignedManagerIds.length === 0) {
      message.warning(t("selectAtLeastOneManager"))
      return
    }

    const first = managersList.find((m: any) => m.id === assignedManagerIds[0])

    createTaskMutation({
      title: taskTitle.trim(),
      description: taskDesc.trim(),
      assignedManagerId: assignedManagerIds[0],
      assignedManagerName: first?.name || first?.userName,
      assignedManagerIds: assignedManagerIds,
      dueDate: dueDate ? dueDate.toISOString() : undefined,
      priority: priority,
      status: "new",
    })
  }

  const openCommentBox = (task: any, status: string) => {
    setCommentTaskId(task.id)
    setCommentStatus(status)
    setCommentText(task.reportText || task.ReportText || task.managerComment || "")
    setFailureCategory(task.failureCategory || task.FailureCategory || "other")
    setIsPartial(!!(task.isPartial ?? task.IsPartial))
    setReviewTaskId(null)
  }

  // Talabnomaga ko'ra menejer natija formasini to'ldirishi SHART —
  // ikkala variantda ham matn majburiy
  const submitComment = () => {
    if (!commentText.trim()) {
      message.warning(
        commentStatus === "completed"
          ? String(t("reportRequired"))
          : String(t("reasonRequired"))
      )
      return
    }
    updateStatusMutation({
      id: commentTaskId as number,
      status: commentStatus,
      managerComment: commentText.trim(),
      reportText: commentText.trim(),
      failureCategory: commentStatus === "failed" ? failureCategory : undefined,
      isPartial: commentStatus === "failed" ? isPartial : false,
    })
  }

  const FAILURE_LABELS: Record<string, string> = {
    client_refused: String(t("reasonClientRefused")),
    no_time: String(t("reasonNoTime")),
    technical: String(t("reasonTechnical")),
    other: String(t("reasonOther")),
  }

  const getPriorityTag = (p: string) => {
    switch (p) {
      case "urgent":
        return <Tag color="error">{t("urgent")}</Tag>
      case "high":
        return <Tag color="volcano">{t("high")}</Tag>
      case "low":
        return <Tag color="default">{t("low")}</Tag>
      default:
        return <Tag color="blue">{t("medium")}</Tag>
    }
  }

  const getStatusTag = (s: string) => {
    switch (s) {
      case "completed":
        return <Tag color="success">{t("statusDone")}</Tag>
      case "failed":
      case "cancelled":
        return <Tag color="error">{t("statusFailed")}</Tag>
      case "in_progress":
        return <Tag color="processing">{t("statusViewed")}</Tag>
      default:
        return <Tag color="warning">{t("statusNew")}</Tag>
    }
  }

  const fieldOf = (item: any, name: string) =>
    item[name] ?? item[name.charAt(0).toUpperCase() + name.slice(1)]

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              backgroundColor: "rgba(37, 99, 235, 0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Clipboard size={20} color="#2563EB" />
          </div>
          <div>
            <Typography.Title level={5} style={{ margin: 0, fontSize: 16 }}>
              {isSuperAdmin ? t("tasksMgmt") : t("myTasks")}
            </Typography.Title>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {isSuperAdmin
                ? t("tasksMgmtSub")
                : t("myTasksSub")}
            </Typography.Text>
          </div>
        </div>
      }
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={860}
      bodyStyle={{ padding: "18px 24px" }}
      centered
      destroyOnClose
    >
      {/* Yangi topshiriq — faqat Glavniy Admin uchun */}
      {isSuperAdmin && (
        <div
          style={{
            padding: "16px",
            backgroundColor: isDark ? "#1E182A" : "#F8FAFC",
            borderRadius: 10,
            border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #E2E8F0",
            marginBottom: 16,
          }}
        >
          <Typography.Text strong style={{ fontSize: 13, display: "block", marginBottom: 8 }}>
            {t("newTaskForManagers")}
          </Typography.Text>

          <div style={{ display: "flex", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
            <Input
              placeholder={t("taskTitlePh")}
              value={taskTitle}
              onChange={(e) => setTaskTitle(e.target.value)}
              style={{ flex: 2, minWidth: 220, borderRadius: 6 }}
            />

            <Select
              mode="multiple"
              placeholder={t("selectManagers")}
              value={assignedManagerIds}
              onChange={(val) => setAssignedManagerIds(val as number[])}
              allowClear
              maxTagCount="responsive"
              optionFilterProp="children"
              style={{ flex: 1, minWidth: 200 }}
            >
              {managersList.map((m: any) => (
                <Select.Option key={m.id} value={m.id}>
                  {m.name || i18n.t("managerNo", { id: m.id })}
                </Select.Option>
              ))}
            </Select>

            <Select value={priority} onChange={(val) => setPriority(val)} style={{ width: 130 }}>
              <Select.Option value="low">{t("low")}</Select.Option>
              <Select.Option value="medium">{t("medium")}</Select.Option>
              <Select.Option value="high">{t("high")}</Select.Option>
              <Select.Option value="urgent">{t("urgent")}</Select.Option>
            </Select>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <Input
              placeholder={t("taskDescPh")}
              value={taskDesc}
              onChange={(e) => setTaskDesc(e.target.value)}
              style={{ flex: 1, minWidth: 220, borderRadius: 6 }}
            />

            <DatePicker
              placeholder={t("deadlinePh")}
              value={dueDate}
              onChange={(d) => setDueDate(d)}
              format="DD.MM.YYYY"
              style={{ width: 150, borderRadius: 6 }}
            />

            {/*
              Admin topshiriqni saqlashdan oldin matnni boshqa tilga o'girib
              olishi mumkin — menejer o'zi tushunadigan tilda oladi.
            */}
            <Select
              value={composeLang}
              onChange={(v) => setComposeLang(v)}
              style={{ width: 120 }}
              options={[
                { value: "uz", label: "O'zbekcha" },
                { value: "ru", label: "Русский" },
                { value: "en", label: "English" },
              ]}
            />
            <Button
              loading={composeTranslating}
              onClick={translateCompose}
              style={{ borderRadius: 6 }}
            >
              {t("translate")}
            </Button>

            <Button
              type="primary"
              icon={<Plus size={15} style={{ marginRight: 4 }} />}
              onClick={handleCreate}
              loading={creating}
              style={{
                backgroundColor: "#2563EB",
                borderColor: "#2563EB",
                borderRadius: 6,
                fontWeight: 600,
              }}
            >
              {t("assignBtn")}
            </Button>
          </div>
        </div>
      )}

      {/* Filtrlar */}
      <div style={{ marginBottom: 12 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 10,
            marginBottom: 8,
          }}
        >
          <Radio.Group
            size="small"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <Radio.Button value="all">{t("all")} ({tasksList.length})</Radio.Button>
            <Radio.Button value="new">{t("unread")}</Radio.Button>
            <Radio.Button value="in_progress">{t("inProgress")}</Radio.Button>
            <Radio.Button value="completed">{t("completedF")}</Radio.Button>
            <Radio.Button value="failed">{t("failedF")}</Radio.Button>
          </Radio.Group>

          <Input
            size="small"
            placeholder={t("taskSearchPh")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onPressEnter={() => setSearchTerm(search.trim())}
            suffix={
              <Search
                size={14}
                style={{ cursor: "pointer" }}
                onClick={() => setSearchTerm(search.trim())}
              />
            }
            allowClear
            style={{ width: 240 }}
          />
        </div>

        {isSuperAdmin && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <DatePicker.RangePicker
              size="small"
              value={createdRange as any}
              onChange={(v) => setCreatedRange(v as any)}
              format="DD.MM.YYYY"
              placeholder={[String(t("givenDateFrom")), String(t("dateTo"))]}
              style={{ width: 250 }}
            />
            <DatePicker.RangePicker
              size="small"
              value={deadlineRange as any}
              onChange={(v) => setDeadlineRange(v as any)}
              format="DD.MM.YYYY"
              placeholder={[String(t("deadlineFrom")), String(t("dateTo"))]}
              style={{ width: 230 }}
            />
            <Select
              size="small"
              placeholder={t("byManager")}
              value={filterManagerId}
              onChange={(val) => setFilterManagerId(val)}
              allowClear
              showSearch
              optionFilterProp="children"
              style={{ width: 190 }}
            >
              {managersList.map((m: any) => (
                <Select.Option key={m.id} value={m.id}>
                  {m.name || i18n.t("managerNo", { id: m.id })}
                </Select.Option>
              ))}
            </Select>
          </div>
        )}
      </div>

      {/* Topshiriqlar ro'yxati */}
      {loadingTasks ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : tasksList.length === 0 ? (
        <Empty description={t("noTasks")} />
      ) : (
        <List
          itemLayout="vertical"
          dataSource={tasksList}
          style={{ maxHeight: 380, overflowY: "auto" }}
          renderItem={(item: any) => {
            const status = fieldOf(item, "status")
            const isRead = !!fieldOf(item, "isRead")
            const isFinished = status === "completed" || status === "failed" || status === "cancelled"
            const managerComment = fieldOf(item, "reportText") || fieldOf(item, "managerComment")
            const dueDateVal = fieldOf(item, "dueDate")
            const readAt = fieldOf(item, "readAt")
            const commenting = commentTaskId === item.id
            const reviewing_ = reviewTaskId === item.id
            const isApproved = !!fieldOf(item, "isApproved")
            const failCat = fieldOf(item, "failureCategory")
            const partial = !!fieldOf(item, "isPartial")
            const reviewCommentVal = fieldOf(item, "reviewComment")
            // Hisobot topshirilgan, lekin admin hali tekshirmagan
            const awaitingReview = isFinished && !isApproved

            return (
              <div
                key={item.id}
                style={{
                  padding: "12px 14px",
                  marginBottom: 8,
                  borderRadius: 10,
                  backgroundColor: isFinished
                    ? isDark
                      ? "#1A1524"
                      : "#F8FAFC"
                    : isDark
                    ? "#231B32"
                    : "#FFFFFF",
                  border: isFinished
                    ? isDark
                      ? "1px solid rgba(255,255,255,0.06)"
                      : "1px solid #E2E8F0"
                    : isDark
                    ? "1px solid rgba(37,99,235,0.3)"
                    : "1px solid #CBD5E1",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 12,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexWrap: "wrap",
                        marginBottom: 4,
                      }}
                    >
                      <Typography.Text
                        strong
                        style={{
                          fontSize: 14,
                          textDecoration: status === "completed" ? "line-through" : "none",
                          color: isFinished ? "#94A3B8" : isDark ? "#FFFFFF" : "#0F172A",
                        }}
                      >
                        {fieldOf(item, "title")}
                      </Typography.Text>
                      {getPriorityTag(fieldOf(item, "priority"))}
                      {getStatusTag(status)}
                      {partial && <Tag color="gold">{t("partiallyDone")}</Tag>}
                      {failCat && status === "failed" && (
                        <Tag color="magenta">{FAILURE_LABELS[failCat] || failCat}</Tag>
                      )}
                      {isFinished &&
                        (isApproved ? (
                          <Tag color="green">{t("adminApproved")}</Tag>
                        ) : (
                          <Tag color="orange">{t("underReview")}</Tag>
                        ))}
                      {fieldOf(item, "assignedManagerName") && (
                        <Tag color="purple">{fieldOf(item, "assignedManagerName")}</Tag>
                      )}
                    </div>

                    {fieldOf(item, "description") && (
                      <Typography.Paragraph
                        type="secondary"
                        style={{ fontSize: 12, margin: "2px 0 6px 0" }}
                      >
                        {taskTranslations[item.id] || fieldOf(item, "description")}
                      </Typography.Paragraph>
                    )}

                    {/* Topshiriqni o'z tiliga o'girish (guruh talabi) */}
                    <Button
                      type="link"
                      size="small"
                      loading={translatingTaskId === item.id}
                      onClick={() =>
                        toggleTaskTranslate(item.id, fieldOf(item, "description") || fieldOf(item, "title"))
                      }
                      style={{ padding: 0, height: 18, fontSize: 11, marginBottom: 4 }}
                    >
                      {taskTranslations[item.id] ? t("showOriginal") : t("translate")}
                    </Button>

                    {managerComment && (
                      <div
                        style={{
                          fontSize: 12,
                          padding: "6px 10px",
                          marginBottom: 6,
                          borderRadius: 6,
                          borderLeft: "3px solid #2563EB",
                          backgroundColor: isDark ? "rgba(37,99,235,0.12)" : "#EFF6FF",
                        }}
                      >
                        <strong>
                          {status === "failed" ? t("failureReason") : t("managerReport")}
                        </strong>{" "}
                        {managerComment}
                      </div>
                    )}

                    {reviewCommentVal && (
                      <div
                        style={{
                          fontSize: 12,
                          padding: "6px 10px",
                          marginBottom: 6,
                          borderRadius: 6,
                          borderLeft: "3px solid #F59E0B",
                          backgroundColor: isDark ? "rgba(245,158,11,0.12)" : "#FFFBEB",
                        }}
                      >
                        <strong>{t("adminComment")}</strong> {reviewCommentVal}
                      </div>
                    )}

                    <div
                      style={{
                        fontSize: 11,
                        color: "#64748B",
                        display: "flex",
                        gap: 12,
                        flexWrap: "wrap",
                      }}
                    >
                      <span>
                        {t("createdBy")}: <strong>{fieldOf(item, "createdByName") || "Admin"}</strong>
                      </span>
                      {dueDateVal && (
                        <span>
                          {t("deadlineLabel")}: <strong>{moment(dueDateVal).format("DD.MM.YYYY")}</strong>
                        </span>
                      )}
                      <span>
                        {t("readLabel")}:{" "}
                        <strong>
                          {isRead
                            ? readAt
                              ? moment(readAt).format("DD.MM.YYYY HH:mm")
                              : "ha"
                            : "yo'q"}
                        </strong>
                      </span>
                    </div>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                    {!isSuperAdmin && !isRead && (
                      <Button
                        size="small"
                        onClick={() => markReadMutation(item.id)}
                        style={{
                          borderRadius: 6,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        <Eye size={14} />
                        {t("iHaveRead")}
                      </Button>
                    )}

                    {!isSuperAdmin && !isFinished && (
                      <>
                        <Button
                          type="primary"
                          size="small"
                          onClick={() => openCommentBox(item, "completed")}
                          style={{
                            borderRadius: 6,
                            backgroundColor: "#10B981",
                            borderColor: "#10B981",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <Check size={14} />
                          {t("statusDone")}
                        </Button>
                        <Button
                          danger
                          size="small"
                          onClick={() => openCommentBox(item, "failed")}
                          style={{
                            borderRadius: 6,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <X size={14} />
                          {t("statusFailed")}
                        </Button>
                      </>
                    )}

                    {/* Admin hisobotni tekshiradi (talabnoma, 4-bosqich) */}
                    {isSuperAdmin && awaitingReview && (
                      <>
                        <Button
                          type="primary"
                          size="small"
                          loading={reviewing}
                          onClick={() =>
                            reviewTaskMutation({ id: item.id, approve: true })
                          }
                          style={{
                            borderRadius: 6,
                            backgroundColor: "#10B981",
                            borderColor: "#10B981",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <Check size={14} />
                          {t("approveBtn")}
                        </Button>
                        <Button
                          size="small"
                          onClick={() => {
                            setReviewTaskId(item.id)
                            setReviewComment(reviewCommentVal || "")
                            setCommentTaskId(null)
                          }}
                          style={{ borderRadius: 6 }}
                        >
                          {t("returnBtn")}
                        </Button>
                      </>
                    )}

                    {isSuperAdmin && (
                      <Popconfirm
                        title={t("deleteTaskConfirm")}
                        onConfirm={() => deleteTaskMutation(item.id)}
                        okText={t("yes")}
                        cancelText={t("no")}
                      >
                        <Button type="text" danger size="small" icon={<Trash2 size={15} />} />
                      </Popconfirm>
                    )}
                  </div>
                </div>

                {commenting && (
                  <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <div style={{ flex: 1 }}>
                      {/* Bajarilmagan bo'lsa sabab toifasi ham ko'rsatiladi */}
                      {commentStatus === "failed" && (
                        <div style={{ display: "flex", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                          <Select
                            size="small"
                            value={failureCategory}
                            onChange={(v) => setFailureCategory(v)}
                            style={{ width: 190 }}
                          >
                            {Object.keys(FAILURE_LABELS).map((k) => (
                              <Select.Option key={k} value={k}>
                                {FAILURE_LABELS[k]}
                              </Select.Option>
                            ))}
                          </Select>
                          <Checkbox
                            checked={isPartial}
                            onChange={(e) => setIsPartial(e.target.checked)}
                          >
                            <span style={{ fontSize: 12 }}>{t("partiallyDone")}</span>
                          </Checkbox>
                        </div>
                      )}
                      <Input.TextArea
                        autoFocus
                        rows={2}
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        placeholder={
                          commentStatus === "completed"
                            ? String(t("reportPh"))
                            : String(t("failurePh"))
                        }
                        style={{ borderRadius: 6 }}
                      />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <Button
                        type="primary"
                        size="small"
                        loading={updatingStatus}
                        onClick={submitComment}
                        style={{ borderRadius: 6 }}
                      >
                        {t("save")}
                      </Button>
                      <Button
                        size="small"
                        onClick={() => {
                          setCommentTaskId(null)
                          setCommentText("")
                        }}
                        style={{ borderRadius: 6 }}
                      >
                        {t("cancel")}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Adminning "qayta ishlashga qaytarish" izohi */}
                {reviewing_ && (
                  <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <Input.TextArea
                      autoFocus
                      rows={2}
                      value={reviewComment}
                      onChange={(e) => setReviewComment(e.target.value)}
                      placeholder={t("returnCommentPh")}
                      style={{ flex: 1, borderRadius: 6 }}
                    />
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <Button
                        type="primary"
                        danger
                        size="small"
                        loading={reviewing}
                        onClick={() => {
                          if (!reviewComment.trim()) {
                            message.warning(t("returnCommentRequired"))
                            return
                          }
                          reviewTaskMutation({
                            id: item.id,
                            approve: false,
                            reviewComment: reviewComment.trim(),
                          })
                        }}
                        style={{ borderRadius: 6 }}
                      >
                        {t("returnBtn")}
                      </Button>
                      <Button
                        size="small"
                        onClick={() => {
                          setReviewTaskId(null)
                          setReviewComment("")
                        }}
                        style={{ borderRadius: 6 }}
                      >
                        {t("cancel")}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )
          }}
        />
      )}
    </Modal>
  )
}
